"use client";
import { getImageUrl } from "@/utils/utils";
import Image from "next/image";
import React from "react";
import { motion } from "framer-motion";
import { useIsMobileOrTab } from "@/hooks/use-mobile-tab";

interface Icon {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: any;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface Card {
  id: number;
  title: string;
  subtitle: string;
  icon: Icon;
}

interface LifeAtNipponKoeiProps {
  title?: string;
  subtitle?: string;
  cards?: Card[];
}

interface BenefitCard {
  title: string;
  description: string;
  icon: React.ReactNode;
}

function BenefitCard({ card }: { card: Card }) {
  return (
    <div className="bg-primary-800 rounded-xl p-8 lg:p-10 h-96 flex flex-col justify-between group hover:bg-primary-800/90 transition-colors duration-300">
      <div className="w-20 h-20 flex-shrink-0">
        <Image
          src={getImageUrl(card.icon.url)}
          alt={card.icon.alternativeText || card.title}
          width={card.icon.width}
          height={card.icon.height}
          className="w-full h-full object-contain"
        />
      </div>
      <div className="space-y-4">
        <h3 className="text-white text-2xl font-medium leading-9 tracking-tight line-clamp-2">
          {card.title}
        </h3>
        <p className="text-white text-lg leading-7 line-clamp-4">
          {card.subtitle}
        </p>
      </div>
    </div>
  );
}

export default function LifeAtNipponKoei({
  title = "Life at Nippon Koei Bangladesh",
  subtitle = "Experience a culture of collaboration, innovation, and growth where your contributions truly make an impact",
  cards = [],
}: LifeAtNipponKoeiProps) {
  const isMobileOrTab = useIsMobileOrTab(740);

  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.section
      className="mx-auto mt-25"
      initial={isMobileOrTab ? false : "hidden"}
      animate={isMobileOrTab ? "visible" : undefined}
      whileInView={isMobileOrTab ? undefined : "visible"}
      viewport={isMobileOrTab ? { once: true } : { once: true, amount: 0.3 }}
      variants={sectionVariants}
    >
      {/* Section Header */}
      <div className="text-center mb-12 lg:mb-[50px]">
        <div className="space-y-4 mb-6">
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-medium text-gray-900 leading-tight tracking-tight">
            {title}
          </h2>
          <p className="text-lg lg:text-xl text-gray-800 font-medium leading-relaxed max-w-3xl mx-auto tracking-tight">
            {subtitle}
          </p>
        </div>
        <div className="w-12 h-0.5 bg-primary-800 mx-auto"></div>
      </div>

      {/* Benefits Cards */}
      <div className="relative bg-white overflow-hidden group/progress">
        {/* Background Pattern */}
        <Image
          src="/images/shared/page-blend-bg.svg"
          alt="Background pattern"
          fill
          className="object-cover object-bottom opacity-30 luminosity mix-blend-luminosity pointer-events-none select-none"
        />

        {/* Content */}
        <div className="relative z-10 p-8 lg:p-12">
          {cards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {cards.map((card) => (
                <BenefitCard key={card.id} card={card} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">
                No benefits information available at the moment.
              </p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="flex h-[2px] bg-gray-25 relative">
          <div className="absolute left-0 top-0 h-[2px] bg-[#1C8579] w-0 group-hover/progress:w-full transition-all duration-500"></div>
        </div>
      </div>
    </motion.section>
  );
}
